import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, Inject, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';

import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { catchError, finalize, map, Observable, of, switchMap, take, tap } from 'rxjs';

import { SvcDialogMode, SvcDialogsModule, SvcDialogService } from '@solvace/component/svc-dialogs';
import { SvcToastService } from '@solvace/component/svc-toast';
import { HttpErrorService } from '@solvace/common/services/http-error.service';
import { LibSharedCommonModule } from 'projects/lib-shared-common/src/public-api';
import { SolvaceRumService, StaticApplicationId, UserEnvironment, UserService } from 'projects/lib-shared-core/src/public-api';
import { SvcButtonsModule } from 'projects/lib-shared-component/src/lib/svc-buttons/svc-buttons.module';
import { SvcBookmarkButtonModule } from '@solvace/feature/general/svc-bookmark-button';
import { SvcReactionChangeEvent, SvcReactionModule } from '@solvace/feature/general/svc-reaction';
import { SvcTotalsStatusComponent, SvcTotalsStatusModule } from '@solvace/feature/general/svc-totals-status';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import {
  ActionPlanChecklistCompletionInfo,
  ActionPlanChecklistResponse,
  SvcActionPlanMenuOption,
  SvcModalActionPlanByIdResponse,
  SvcModalActionPlanConfig,
  SvcModalActionPlanCopyHeaderData,
  SvcModalActionPlanCreateResponse,
  SvcModalActionPlanEscalateRequest,
} from './shared/interfaces/svc-modal-action-plan.interface';
import { SvcModalActionPlanStatusTypeById } from './shared/enums/svc-modal-action-plan-status.enum';
import { SvcModalActionPlanTab } from './shared/enums/svc-modal-action-plan-tab.enum';
import { SvcModalActionPlanService } from './shared/services/svc-modal-action-plan.service';
import { SvcActionPlanApprovalService } from './shared/services/svc-action-plan-approval.service';
import { SvcActionPlanChecklistService } from './shared/services/svc-action-plan-checklist.service';
import { SvcActionPlanPermissionService } from './shared/services/svc-action-plan-permission.service';
import { SvcActionPlanRefreshService } from './shared/services/svc-action-plan-refresh.service';
import { SvcActionPlanFormComponent } from './shared/components/svc-action-plan-form/svc-action-plan-form.component';
import { SvcActionPlanApprovalButtonComponent } from './shared/components/svc-action-plan-approval-button/svc-action-plan-approval-button.component';
import { SvcActionPlanFeedTabComponent } from './shared/components/svc-action-plan-feed-tab/svc-action-plan-feed-tab.component';
import { SvcActionPlanChecklistTabComponent } from './shared/components/svc-action-plan-checklist-tab/svc-action-plan-checklist-tab.component';
import { SvcActionPlanApprovalTabComponent } from './shared/components/svc-action-plan-approval-tab/svc-action-plan-approval-tab.component';
import { SvcActionPlanHistoryTabComponent } from './shared/components/svc-action-plan-history-tab/svc-action-plan-history-tab.component';
import {
  SvcActionPlanEscalateModalComponent,
  SvcActionPlanEscalateModalData,
  SvcActionPlanEscalateModalResult,
} from './shared/components/svc-action-plan-escalate-modal/svc-action-plan-escalate-modal.component';
import {
  SvcActionPlanRescheduleModalComponent,
  SvcActionPlanRescheduleModalData,
  SvcActionPlanRescheduleModalResult,
} from './shared/components/svc-action-plan-reschedule-modal/svc-action-plan-reschedule-modal.component';
import {
  ObeyaReferenceType,
  buildObeyaSystemReferences,
  isObeyaApplicationId,
  isObeyaSystemSelected,
  resolveIsObeyaContext,
  resolveLatestEscalatedSiteId,
  resolveObeyaPrefillReference,
} from './shared/utils/actionplan-obeya-reference.utils';
import { SvcPrinterService } from '../../general/svc-printer/svc-printer.service';
import { buildActionPlanPrintOptions, buildChecklistPrintElement, compactSectionForPrint } from './shared/utils/actionplan-print-options.utils';
import { SvcActionPlanCounterService } from './shared/services/svc-action-plan-counter.service';

@Component({
  selector: 'svc-modal-action-plan',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    SvcDialogsModule,
    SvcButtonsModule,
    MatMenuModule,
    MatIconModule,
    MatTabsModule,
    LibSharedCommonModule,
    SvcActionPlanFormComponent,
    SvcActionPlanApprovalButtonComponent,
    SvcActionPlanFeedTabComponent,
    SvcActionPlanChecklistTabComponent,
    SvcActionPlanApprovalTabComponent,
    SvcActionPlanHistoryTabComponent,
    SvcBookmarkButtonModule,
    SvcReactionModule,
    SvcTotalsStatusModule,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './svc-modal-action-plan.component.html',
  styleUrls: ['./svc-modal-action-plan.component.scss'],
})
export class SvcModalActionPlanComponent implements OnInit, AfterViewInit {
  @ViewChild('actionPlanForm') public actionPlanForm?: SvcActionPlanFormComponent;
  @ViewChild('checklistTab') public checklistTab?: SvcActionPlanChecklistTabComponent;
  @ViewChild('feedTab') public feedTab?: SvcActionPlanFeedTabComponent;
  @ViewChild(SvcTotalsStatusComponent) public totalsStatus?: SvcTotalsStatusComponent;

  // Host element refs used only to capture each printable section's DOM. We read
  // the component host (never the [hidden] wrapper div) so the cloned node the
  // printer renders is not display:none even when its tab is inactive.
  @ViewChild('actionPlanForm', { read: ElementRef }) public actionPlanFormEl?: ElementRef<HTMLElement>;
  @ViewChild('checklistTab', { read: ElementRef }) public checklistTabEl?: ElementRef<HTMLElement>;
  @ViewChild('feedTab', { read: ElementRef }) public feedTabEl?: ElementRef<HTMLElement>;
  @ViewChild('approvalTab', { read: ElementRef }) public approvalTabEl?: ElementRef<HTMLElement>;
  @ViewChild('historyTab', { read: ElementRef }) public historyTabEl?: ElementRef<HTMLElement>;

  #destroyRef = inject(DestroyRef);
  #dialog = inject(MatDialog);
  #dialogService = inject(SvcDialogService);
  #userService = inject(UserService);
  #toastService = inject(SvcToastService);
  #errorService = inject(HttpErrorService);
  #translocoService = inject(TranslocoService);
  #modalActionPlanService = inject(SvcModalActionPlanService);
  #approvalService = inject(SvcActionPlanApprovalService);
  #checklistService = inject(SvcActionPlanChecklistService);
  #permissions = inject(SvcActionPlanPermissionService);
  #refresh = inject(SvcActionPlanRefreshService);
  #printerService = inject(SvcPrinterService);
  #counterService = inject(SvcActionPlanCounterService);
  #rum = inject(SolvaceRumService);
  #rumOpenedAt = this.#rum.startTimer();
  #rumSubmitStartedAt: number | null = null;
  #rumOpenPending = false;
  #rumCombosLoaded = false;
  #rumRecordLoaded = false;

  public environment: UserEnvironment = this.#userService.environment;
  public isLoading = false;
  public modalModes = SvcDialogMode;
  #hasChanges = false;
  #refreshSynced = false;

  public get hasChanges(): boolean {
    return this.#hasChanges;
  }
  public set hasChanges(value: boolean) {
    this.#hasChanges = value;
    if (value) this.#refreshSynced = false;
  }
  public dialogActionOptions: SvcActionPlanMenuOption[] = [];
  public loadedActionPlan: SvcModalActionPlanByIdResponse | null = null;
  public loadedChecklist: ActionPlanChecklistResponse | null = null;

  public readonly applicationId = StaticApplicationId.actionPlanRevamp;
  public readonly featureName = 'view-action-plan';
  public readonly commentConfigType = 'A59245C1-7D4E-4B3A-9F2C-1E8D7C6B5A40';
  public isReactionLoading = false;
  public readonly trackByActionOption = (index: number, option: SvcActionPlanMenuOption): string => `${option?.name ?? ''}-${index}`;

  constructor(
    public dialogRef: MatDialogRef<SvcModalActionPlanComponent>,
    @Inject(MAT_DIALOG_DATA) public modalConfig: SvcModalActionPlanConfig
  ) {
    this.dialogRef.disableClose = true;
    this.modalConfig = {
      ...this.modalConfig,
      mode: this.modalConfig?.mode ?? this.modalModes.add,
    };
    this.#setMoreOptions();
  }

  public ngOnInit(): void {
    this.#modalActionPlanService.resetModalState();

    const initialTab = this.modalConfig?.initialTab;
    if (initialTab != null && !this.isAddMode && Object.values(SvcModalActionPlanTab).includes(initialTab)) {
      this.#modalActionPlanService.selectModalTab(initialTab);
    }

    this.#registerDismissRefresh();
    this.#trackRumOpen();
  }

  public onFormCombosLoaded(): void {
    if (this.#rumCombosLoaded) return;
    this.#rumCombosLoaded = true;

    if (this.isAddMode && !this.modalConfig?.copyData) {
      this.#rum.trackDuration('record.create-loaded', this.#rumOpenedAt, { module: 'action-plan', feature: 'new-record' });
      return;
    }

    this.#trackRumOpenLoaded();
  }

  #trackRumOpen(): void {
    if (this.modalConfig?.copyData) return;

    if (this.isAddMode) {
      this.#rum.track('record.create', { module: 'action-plan', feature: 'new-record' });
      return;
    }

    this.#rumOpenPending = !!this.modalConfig?.id;
  }

  #trackRumOpenLoaded(): void {
    if (!this.#rumOpenPending || !this.#rumCombosLoaded || !this.#rumRecordLoaded) return;
    this.#rumOpenPending = false;
    this.#rum.trackDuration('record.open-loaded', this.#rumOpenedAt, { module: 'action-plan' });
  }

  #trackRumRecordLoaded(): void {
    if (this.#rumSubmitStartedAt !== null) {
      this.#trackRumSubmitLoaded();
      return;
    }

    this.#rumRecordLoaded = true;
    this.#trackRumOpenLoaded();
  }

  #trackRumSubmitLoaded(): void {
    if (this.#rumSubmitStartedAt === null) return;
    this.#rum.trackDuration('record.submit-loaded', this.#rumSubmitStartedAt, { module: 'action-plan', feature: 'new-record' });
    this.#rumSubmitStartedAt = null;
  }

  #registerDismissRefresh(): void {
    this.dialogRef
      .beforeClosed()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        if (result == null && this.hasChanges && !this.#refreshSynced) this.#refresh.requestRefresh();
      });
  }

  public ngAfterViewInit(): void {
    if (this.modalConfig?.copyData) {
      this.modalConfig.mode = this.modalModes.add;
      this.modalConfig.id = undefined;
      this.#applyLoadedActionPlan(null);
      this.actionPlanForm?.applyCopyHeaderData(this.modalConfig.copyData);
      return;
    }

    if (this.isAddMode && this.modalConfig?.lockedSystemUniqueId) {
      this.actionPlanForm?.lockSystemByUniqueId(this.modalConfig.lockedSystemUniqueId);
    } else if (this.isAddMode && this.modalConfig?.lockedSystemApplicationId) {
      this.actionPlanForm?.lockSystemByApplicationId(this.modalConfig.lockedSystemApplicationId);
    }

    this.#registerView();
    this.#loadActionPlanById();
  }

  #registerView(): void {
    if (this.isAddMode) return;
    const actionPlanUniqueId = Number(this.modalConfig?.id ?? 0);
    if (!Number.isFinite(actionPlanUniqueId) || actionPlanUniqueId <= 0) return;

    this.#counterService
      .registerView(actionPlanUniqueId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.totalsStatus?.refresh({ views: true });
        this.#refresh.notifyRowTotalsUpdated(actionPlanUniqueId);
      });
  }

  public get isAddMode(): boolean {
    return this.modalConfig?.mode === this.modalModes.add;
  }

  public get isEditMode(): boolean {
    return this.modalConfig?.mode === this.modalModes.edit;
  }

  public get actionPlanTabs(): typeof SvcModalActionPlanTab {
    return SvcModalActionPlanTab;
  }

  public get tabIndex(): SvcModalActionPlanTab {
    return this.#modalActionPlanService.modalTabIndex();
  }

  public get totalComments(): number {
    return this.#modalActionPlanService.modalTotalComments();
  }

  public get historyRefreshTick(): number {
    return this.#modalActionPlanService.modalHistoryRefreshTick();
  }

  public get showMoreOptions(): boolean {
    return this.dialogActionOptions.some((option) => !option.hidden);
  }

  public get isEscalatedOutsideOriginSite(): boolean {
    return this.#permissions.isSiteRestrictedAfterEscalation(this.loadedActionPlan);
  }

  public getMoreOptionTestId(option: SvcActionPlanMenuOption): string {
    switch (option?.customIcon) {
      case 'copy':
        return 'ap-form__btn--more-option-copy';
      case 'escalate':
        return 'ap-form__btn--more-option-escalate';
      case 'clock':
        return 'ap-form__btn--more-option-reschedule';
    }

    switch (option?.icon) {
      case 'heroicons_solid:printer':
        return 'ap-form__btn--more-option-print';
      case 'heroicons_solid:trash':
        return 'ap-form__btn--more-option-remove';
      case 'heroicons_solid:x-circle':
        return 'ap-form__btn--more-option-cancel';
      case 'heroicons_solid:pencil-square':
        return 'ap-form__btn--more-option-edit';
      default:
        return 'ap-form__btn--more-option';
    }
  }

  public get canDeleteActionPlan(): boolean {
    if (!this.loadedActionPlan) return false;
    if (this.modalConfig?.isDraft) return this.#permissions.hasActionPlanAdminRole();
    return this.#permissions.canDelete(this.loadedActionPlan);
  }

  public get canUseWorkflowActions(): boolean {
    return !!this.loadedActionPlan && this.#permissions.canEdit(this.loadedActionPlan);
  }

  // Drives the "Editar" entry in the overflow menu. Concluded and under-
  // approval plans block the entry point so users can't enter edit-mode and
  // mutate fields that should stay frozen until reopen/approval lands.
  public get canSwitchToEditMode(): boolean {
    return !!this.loadedActionPlan && this.#permissions.canSwitchToEditMode(this.loadedActionPlan);
  }

  // Plan status helpers — pulled from the permission service so the modal,
  // the approval tab and the checklist tab consume the same single rule.
  public get isPlanConcluded(): boolean {
    return this.#permissions.isPlanConcluded(this.loadedActionPlan);
  }

  public get isPlanUnderApproval(): boolean {
    return this.#permissions.isPlanUnderApproval(this.loadedActionPlan);
  }

  public get isPlanReadonlyByStatus(): boolean {
    return this.#permissions.isPlanReadonlyByStatus(this.loadedActionPlan);
  }

  public get canEditApprover(): boolean {
    if (!this.isAddMode && !this.isEditMode) return false;
    return this.#permissions.canModifyApprover(this.loadedActionPlan);
  }

  public get canCancelActionPlan(): boolean {
    return !!this.loadedActionPlan && this.#permissions.canCancel(this.loadedActionPlan);
  }

  public get canEscalateActionPlan(): boolean {
    return !!this.loadedActionPlan && this.#permissions.canEscalate(this.loadedActionPlan);
  }

  public get isObeyaContext(): boolean {
    return resolveIsObeyaContext({
      plan: this.loadedActionPlan,
      applicationId: this.modalConfig?.notificationParams?.applicationId ?? this.modalConfig?.applicationId,
    });
  }

  public get canRescheduleActionPlan(): boolean {
    return !!this.loadedActionPlan && this.#permissions.canReschedule(this.loadedActionPlan);
  }

  public get showFinalizeButton(): boolean {
    if (this.isAddMode || this.isEditMode || !this.loadedActionPlan) return false;
    if (this.showChecklistOnlySave) return false;
    const statusId = Number(this.loadedActionPlan?.statusId ?? 0);
    const statusType = SvcModalActionPlanStatusTypeById[statusId];
    if (!['ontime', 'late'].includes(statusType)) return false;
    return this.#permissions.canFinalize(this.loadedActionPlan, { requireApprover: false });
  }

  public get showApprovalButton(): boolean {
    if (this.isAddMode || this.isEditMode) return false;
    if (this.showChecklistOnlySave) return false;
    const statusId = Number(this.loadedActionPlan?.statusId ?? 0);
    const statusType = SvcModalActionPlanStatusTypeById[statusId];
    if (statusType !== 'approval') return false;
    if (!this.#permissions.hasAssignedApprover(this.loadedActionPlan)) return false;
    return this.#permissions.canApprove(this.loadedActionPlan);
  }

  public get showReopenButton(): boolean {
    if (this.isAddMode || this.isEditMode || !this.loadedActionPlan) return false;
    const statusId = Number(this.loadedActionPlan?.statusId ?? 0);
    if (SvcModalActionPlanStatusTypeById[statusId] !== 'completed') return false;
    return this.#permissions.canReopen(this.loadedActionPlan);
  }

  public get isSaveBlocked(): boolean {
    return this.isLoading || !this.actionPlanForm?.validForms;
  }

  public get checklistInfo(): ActionPlanChecklistCompletionInfo | null {
    const fromTab = this.checklistTab?.checklistInfo();
    if (fromTab) return fromTab;
    if (!this.loadedChecklist) return null;
    return {
      total: this.loadedChecklist.totalCount,
      completed: this.loadedChecklist.completedCount,
    };
  }

  public get showChecklistOnlySave(): boolean {
    return false;
  }

  public selectTab(tab: SvcModalActionPlanTab): void {
    this.#modalActionPlanService.selectModalTab(tab);
    if (tab === SvcModalActionPlanTab.History) {
      this.refreshHistory();
    }
  }

  public onCommentsTotalChange(total: number): void {
    this.#modalActionPlanService.setModalCommentsTotal(total);
  }

  /** Fired by the feed tab only on a real user comment add/remove. */
  public onCommentsChanged(): void {
    this.totalsStatus?.refresh({ comments: true });
    this.#notifyRowTotalsUpdated();
  }

  // ─── interactions strip handlers (bookmark / reaction) ────────────────────

  public onBookmarkChanged(value: boolean): void {
    const messageKey = value ? 'adicionado aos' : 'removido dos';
    this.#toastService.success(this.#translocoService.translate(`Registro ${messageKey} favoritos com sucesso`));
    this.totalsStatus?.refresh({ reactions: true });
    this.#notifyRowTotalsUpdated();

    const actionPlanUniqueId = this.#resolveCurrentActionPlanUniqueId();
    if (Number.isFinite(actionPlanUniqueId) && actionPlanUniqueId > 0) {
      this.#refresh.notifyRowBookmarkUpdated(actionPlanUniqueId, value);
    }
  }

  public onReactionChange(event: SvcReactionChangeEvent): void {
    if (!this.totalsStatus) return;
    (event?.anticipated?.onReactionDone$ ?? of({ success: true }))
      .pipe(
        take(1),
        tap((res) => {
          if (res?.success) {
            this.totalsStatus?.refresh({ reactions: true, views: true, comments: true });
            this.#notifyRowTotalsUpdated();
          }
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  /** Footer comment icon → jump to the Feed tab (matches unsafe-condition UX). */
  public goToFeedTab(): void {
    this.selectTab(SvcModalActionPlanTab.Feed);
  }

  public refreshHistory(): void {
    const actionPlanUniqueId = this.#resolveCurrentActionPlanUniqueId();
    if (!Number.isFinite(actionPlanUniqueId) || actionPlanUniqueId <= 0) return;
    this.#modalActionPlanService.refreshModalHistory();
  }

  // Opens the shared print modal (RCA pattern). Section order, default selection
  // and the no-additional-items rule live in buildActionPlanPrintOptions. Each
  // section captures its component host element so the printer can clone it
  // regardless of which tab is currently active.
  public print(): void {
    // The tab sections stay in the DOM via [hidden] (display:none) and keep
    // their rendered content, so the printer can clone each host even while its
    // tab is inactive. History loads lazily on first open, so trigger it here to
    // populate the (hidden) timeline before the user prints it.
    this.refreshHistory();

    const options = buildActionPlanPrintOptions((key) => this.#translocoService.translate(key), {
      form: this.actionPlanFormEl?.nativeElement,
      checklist: buildChecklistPrintElement(this.checklistTabEl?.nativeElement),
      approval: compactSectionForPrint(this.approvalTabEl?.nativeElement),
      history: compactSectionForPrint(this.historyTabEl?.nativeElement),
      feed: compactSectionForPrint(this.feedTabEl?.nativeElement),
    });

    this.#printerService.open({ options });
  }

  public finalizeActionPlan(): void {
    const text = this.#translocoService.translate('Tem certeza que deseja finalizar?');
    this.#dialogService
      .openConfirm(text)
      .pipe(take(1))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.#executeFinalize();
      });
  }

  #executeFinalize(): void {
    const actionPlanUniqueId = this.#resolveCurrentActionPlanUniqueId();
    if (!Number.isFinite(actionPlanUniqueId) || actionPlanUniqueId <= 0 || this.isLoading) return;

    this.isLoading = true;
    const hasApprover = this.#permissions.hasAssignedApprover(this.loadedActionPlan);
    const workflow$ = hasApprover
      ? this.#approvalService.sendToApproval(actionPlanUniqueId, { comments: '' })
      : this.#approvalService.complete(actionPlanUniqueId, { comments: '' });

    workflow$
      .pipe(
        switchMap(() => this.#modalActionPlanService.getActionPlanById(actionPlanUniqueId)),
        tap((actionPlan) => {
          const resolvedActionPlan = this.#preserveApproverFromCurrentActionPlan(actionPlan);
          this.#applyLoadedActionPlan(resolvedActionPlan);
          this.actionPlanForm?.patchFormActionPlan(resolvedActionPlan);
          this.#dispatchEventToLegacy('EndedPlanCallback', actionPlanUniqueId, resolvedActionPlan?.systemUniqueId);
          this.refreshHistory();
          this.hasChanges = true;
          this.#toastService.success(this.#translocoService.translate('Plano concluído com sucesso'));
        }),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of(null);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public approveActionPlan(): void {
    const text = this.#translocoService.translate('Tem certeza que deseja aprovar?');
    this.#dialogService
      .openConfirm(text)
      .pipe(take(1))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.#executeApprove();
      });
  }

  #executeApprove(): void {
    const actionPlanUniqueId = this.#resolveCurrentActionPlanUniqueId();
    if (!Number.isFinite(actionPlanUniqueId) || actionPlanUniqueId <= 0 || this.isLoading) return;

    this.isLoading = true;
    this.#approvalService
      .approve(actionPlanUniqueId, { comments: '' })
      .pipe(
        switchMap(() => this.#modalActionPlanService.getActionPlanById(actionPlanUniqueId)),
        tap((actionPlan) => {
          const resolvedActionPlan = this.#preserveApproverFromCurrentActionPlan(actionPlan);
          this.#applyLoadedActionPlan(resolvedActionPlan);
          this.actionPlanForm?.patchFormActionPlan(resolvedActionPlan);
          this.#dispatchEventToLegacy('ApprovedPlanCallback', actionPlanUniqueId, resolvedActionPlan?.systemUniqueId);
          this.refreshHistory();
          this.hasChanges = true;
          this.#toastService.success(this.#translocoService.translate('Plano aprovado com sucesso'));
        }),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of(null);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public rejectActionPlan(): void {
    const text = this.#translocoService.translate('Tem certeza que deseja reprovar?');
    this.#dialogService
      .openConfirm(text)
      .pipe(take(1))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.#executeReject();
      });
  }

  #executeReject(): void {
    const actionPlanUniqueId = this.#resolveCurrentActionPlanUniqueId();
    if (!Number.isFinite(actionPlanUniqueId) || actionPlanUniqueId <= 0 || this.isLoading) return;

    this.isLoading = true;
    this.#approvalService
      .reject(actionPlanUniqueId, { comments: '' })
      .pipe(
        switchMap(() => this.#modalActionPlanService.getActionPlanById(actionPlanUniqueId)),
        tap((actionPlan) => {
          const resolvedActionPlan = this.#preserveApproverFromCurrentActionPlan(actionPlan);
          this.#applyLoadedActionPlan(resolvedActionPlan);
          this.actionPlanForm?.patchFormActionPlan(resolvedActionPlan);
          this.refreshHistory();
          this.hasChanges = true;
          this.#dispatchEventToLegacy('DisapprovedPlanCallback', actionPlanUniqueId, resolvedActionPlan?.systemUniqueId);
          this.#toastService.success(this.#translocoService.translate('Plano rejeitado com sucesso'));
        }),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of(null);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public reopenActionPlan(): void {
    const text = this.#translocoService.translate('Tem certeza que deseja reabrir?');
    this.#dialogService
      .openConfirm(text)
      .pipe(take(1))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.#executeReopen();
      });
  }

  #executeReopen(): void {
    const actionPlanUniqueId = this.#resolveCurrentActionPlanUniqueId();
    if (!Number.isFinite(actionPlanUniqueId) || actionPlanUniqueId <= 0 || this.isLoading) return;

    this.isLoading = true;
    this.#approvalService
      .reopen(actionPlanUniqueId, { comments: '' })
      .pipe(
        switchMap(() => this.#modalActionPlanService.getActionPlanById(actionPlanUniqueId)),
        tap((actionPlan) => {
          this.#applyLoadedActionPlan(actionPlan);
          this.actionPlanForm?.patchFormActionPlan(actionPlan);
          this.#dispatchEventToLegacy('ReopenedPlanCallback', actionPlanUniqueId, actionPlan?.systemUniqueId);
          this.refreshHistory();
          this.hasChanges = true;
          this.#toastService.success(this.#translocoService.translate('Plano reaberto com sucesso'));
        }),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of(null);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  /**
   * Single entry-point for setting `loadedActionPlan`. Always rebuilds the
   * overflow menu so its visibility rules stay in sync with the latest plan
   * status — without this every workflow action (approve / reject / finalize
   * / reopen / escalate / reschedule) would have to remember to call
   * `#setMoreOptions()` after the GET resolves, and forgetting it left the
   * menu stuck on the previous status (e.g. "Remove + Cancel" after a
   * reopen).
   */
  #applyLoadedActionPlan(plan: SvcModalActionPlanByIdResponse | null): void {
    // Obeya-hosted only: carry the host id into the plan for context-aware helpers.
    // Site-origin permissions are resolved from escalationHistories.
    const hostApplicationId = this.modalConfig?.notificationParams?.applicationId ?? this.modalConfig?.applicationId;
    this.loadedActionPlan = plan && isObeyaApplicationId(hostApplicationId) ? { ...plan, hostApplicationId } : plan;
    this.#setMoreOptions();
  }

  #setMoreOptions(): void {
    const isReadonlyByStatus = this.isPlanReadonlyByStatus;
    const isConcluded = this.isPlanConcluded;
    const isEscalatedOutsideOriginSite = this.isEscalatedOutsideOriginSite;
    this.dialogActionOptions = [
      {
        name: this.#translocoService.translate('Imprimir'),
        icon: 'heroicons_solid:printer',
        action: (): void => this.print(),
        hidden: isEscalatedOutsideOriginSite || !this.loadedActionPlan,
      },
      {
        name: this.#translocoService.translate('Remove'),
        icon: 'heroicons_solid:trash',
        action: (): void => this.confirmDeleteActionPlan(),
        hidden: isEscalatedOutsideOriginSite || !this.canDeleteActionPlan,
      },
      {
        name: this.#translocoService.translate('Copy'),
        customIcon: 'copy',
        action: (): void => this.copyActionPlan(),
        hidden: isEscalatedOutsideOriginSite || !this.loadedActionPlan || isReadonlyByStatus,
      },
      {
        name: this.#translocoService.translate('Cancel'),
        icon: 'heroicons_solid:x-circle',
        action: (): void => this.confirmCancelActionPlan(),
        hidden: isEscalatedOutsideOriginSite || !this.canCancelActionPlan || isConcluded,
      },
      {
        name: this.#translocoService.translate('Editar'),
        icon: 'heroicons_solid:pencil-square',
        action: (): void => this.openEditMode(),
        hidden: isEscalatedOutsideOriginSite || !this.canSwitchToEditMode || this.isEditMode,
      },
      {
        name: this.#translocoService.translate('Escalar'),
        customIcon: 'escalate',
        action: (): void => this.openEscalateModal(),
        hidden: isEscalatedOutsideOriginSite || this.isAddMode || !this.canEscalateActionPlan || isReadonlyByStatus,
      },
      {
        name: this.#translocoService.translate('Reprogramar'),
        customIcon: 'clock',
        action: (): void => this.openRescheduleModal(),
        hidden: isEscalatedOutsideOriginSite || this.isAddMode || !this.canRescheduleActionPlan || isReadonlyByStatus,
      },
    ];
  }

  public openEscalateModal(): void {
    const selectedSystemId =
      this.actionPlanForm?.selectedSystemUniqueId ?? this.actionPlanForm?.formActionPlan?.getRawValue()?.systemId?.[0] ?? this.loadedActionPlan?.systemUniqueId ?? null;
    const selectedSystem = this.actionPlanForm?.optionsCombos?.systems?.find((option) => `${option?.value}` === `${selectedSystemId}`);
    const selectedSystemValue = selectedSystem?.value ?? selectedSystemId;

    const data: SvcActionPlanEscalateModalData = {
      usersOptions: this.actionPlanForm?.usersOptions ?? [],
      systemsOptions: this.actionPlanForm?.optionsCombos?.systems ?? [],
      selectedResponsibleIds: this.actionPlanForm?.formActionPlan?.controls?.responsibleId?.value ?? [],
      selectedSystemIds: selectedSystemValue !== null && selectedSystemValue !== undefined && selectedSystemValue !== '' ? [selectedSystemValue] : [],
      systemName: String(selectedSystem?.label ?? this.actionPlanForm?.selectedSystemLabel ?? ''),
      canChangeSystem: this.isObeyaContext ? false : this.loadedActionPlan?.isInternalSystem !== true,
      systemId: Number(selectedSystemValue ?? 0) || null,
      showVoiceToTextButton: this.actionPlanForm?.showVoiceToTextButton ?? true,
      isObeyaContext: this.isObeyaContext,
      applicationId: this.isObeyaContext ? StaticApplicationId.digitalObeya : (this.modalConfig?.notificationParams?.applicationId ?? this.modalConfig?.applicationId),
      planSiteId: this.loadedActionPlan?.siteId ?? null,
      planRoomId: resolveObeyaPrefillReference(this.loadedActionPlan, ObeyaReferenceType.Room),
      planBoardId: resolveObeyaPrefillReference(this.loadedActionPlan, ObeyaReferenceType.Board),
      escalatedSiteId: resolveLatestEscalatedSiteId(this.loadedActionPlan),
    };

    const sizing = this.isObeyaContext
      ? { width: '600px', minWidth: '600px', maxWidth: '600px', height: 'auto', maxHeight: '90vh' }
      : { width: '400px', minWidth: '400px', maxWidth: '400px', height: '364px', minHeight: '364px', maxHeight: '364px' };
    const panelClass = this.isObeyaContext ? ['action-plan-escalate-dialog', 'action-plan-escalate-dialog--obeya'] : ['action-plan-escalate-dialog'];

    this.#dialog
      .open<SvcActionPlanEscalateModalComponent, SvcActionPlanEscalateModalData, SvcActionPlanEscalateModalResult>(SvcActionPlanEscalateModalComponent, {
        ...sizing,
        autoFocus: false,
        disableClose: true,
        panelClass,
        data,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (!result) return;
        this.#applyEscalateUpdate(result);
      });
  }

  public openRescheduleModal(): void {
    const currentDueDate = this.actionPlanForm?.formActionPlan?.controls?.dueDate?.value ?? null;

    const data: SvcActionPlanRescheduleModalData = {
      dueDate: currentDueDate,
      showVoiceToTextButton: this.actionPlanForm?.showVoiceToTextButton ?? true,
    };

    this.#dialog
      .open<SvcActionPlanRescheduleModalComponent, SvcActionPlanRescheduleModalData, SvcActionPlanRescheduleModalResult>(SvcActionPlanRescheduleModalComponent, {
        width: '400px',
        minWidth: '400px',
        maxWidth: '400px',
        height: 'auto',
        minHeight: 'auto',
        maxHeight: '364px',
        autoFocus: false,
        disableClose: true,
        panelClass: ['action-plan-reschedule-dialog'],
        data,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (!result?.dueDate) return;
        this.#applyRescheduleUpdate(result);
      });
  }

  public copyActionPlan(): void {
    const value = this.actionPlanForm?.formActionPlan?.getRawValue?.();
    if (!value) return;

    const copyData: SvcModalActionPlanCopyHeaderData = {
      description: value?.description ?? null,
      typeId: this.#toNullableNumber(value?.typeId?.[0]),
      priorityId: this.#toNullableNumber(value?.priorityId?.[0]),
      systemId: this.#toNullableNumber(value?.systemId?.[0]),
      responsibleId: value?.responsibleId?.[0] ?? null,
      actors: Array.isArray(value?.actors) ? value.actors : [],
      confidential: !!value?.confidential,
    };

    this.#dialog.open(SvcModalActionPlanComponent, {
      width: '800px',
      maxWidth: '800px',
      height: '1000px',
      maxHeight: '1000px',
      autoFocus: false,
      disableClose: true,
      data: {
        mode: this.modalModes.add,
        copyData,
      } as SvcModalActionPlanConfig,
    });

    this.dialogRef.close({ hadChanges: this.hasChanges });
  }

  public saveActionPlan(): void {
    if (this.isLoading) return;

    if (this.checklistTab?.hasEmptyChecklistInput()) {
      this.checklistTab.showFillFieldAlert();
      return;
    }

    if (!this.isAddMode && !this.isEditMode) return;

    this.actionPlanForm?.markAllAsTouchedForms();
    if (!this.actionPlanForm?.validForms) return;

    if (this.isAddMode) {
      this.#createActionPlan();
      return;
    }

    this.#updateActionPlan();
  }

  public onChecklistPersisted(): void {
    this.hasChanges = true;
  }

  #createActionPlan(): void {
    this.isLoading = true;
    this.#rumSubmitStartedAt = this.#rum.startTimer();
    this.#modalActionPlanService
      .getObeyaSystemUniqueIds()
      .pipe(
        switchMap((obeyaSystemUniqueIds) => {
          const formValue = this.actionPlanForm.formValue;
          const createPayload = {
            ...formValue,
            ...(this.modalConfig?.notificationParams ?? {}),
            isDraft: this.modalConfig?.isDraft ?? false,
            isObeya: this.isObeyaContext || isObeyaSystemSelected(formValue.systemId, obeyaSystemUniqueIds),
          };
          return this.#modalActionPlanService.createActionPlan(createPayload);
        }),
        switchMap((response) => this.#resolveCreatedActionPlanUniqueId(response).pipe(map((actionPlanUniqueId) => ({ response, actionPlanUniqueId })))),
        switchMap(({ response, actionPlanUniqueId }) => this.#saveCustomFieldsByRecordId(actionPlanUniqueId ?? 0).pipe(map(() => ({ response, actionPlanUniqueId })))),
        switchMap(({ response, actionPlanUniqueId }) => {
          return actionPlanUniqueId
            ? (this.checklistTab?.saveChecklist(actionPlanUniqueId).pipe(map(() => ({ response, actionPlanUniqueId }))) ?? of({ response, actionPlanUniqueId }))
            : of({ response, actionPlanUniqueId });
        }),
        tap(({ response, actionPlanUniqueId }) => this.#onSaveSuccess(response, actionPlanUniqueId)),
        catchError((err) => {
          this.#rumSubmitStartedAt = null;
          this.#errorService.showErrorInToast(err);
          return of(null);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #updateActionPlan(): void {
    const actionPlanForm = this.actionPlanForm;
    const actionPlanUniqueId = this.#resolveCurrentActionPlanUniqueId();
    if (!Number.isFinite(actionPlanUniqueId) || actionPlanUniqueId <= 0 || !this.loadedActionPlan || !actionPlanForm) return;

    this.isLoading = true;
    this.#modalActionPlanService
      .updateActionPlan(actionPlanUniqueId, this.loadedActionPlan, actionPlanForm.formValue, undefined, this.modalConfig?.isDraft ?? false)
      .pipe(
        switchMap(() => this.#saveCustomFieldsByRecordId(actionPlanUniqueId)),
        switchMap(() => this.checklistTab?.saveChecklist(actionPlanUniqueId) ?? of(true)),
        switchMap(() => this.#modalActionPlanService.getActionPlanById(actionPlanUniqueId, this.modalConfig?.isDraft ?? false)),
        tap((actionPlan) => this.#onUpdateSuccess(actionPlan)),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of(null);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #onSaveSuccess(response: SvcModalActionPlanCreateResponse | number | null, createdActionPlanUniqueId: number | null = null): void {
    if (!response) {
      this.#rumSubmitStartedAt = null;
      return;
    }
    this.#rum.track('record.submit', { module: 'action-plan', feature: 'new-record' });
    this.hasChanges = true;
    this.actionPlanForm?.setCreatedState(response);
    this.actionPlanForm?.setApprovalDate(typeof response !== 'number' ? response?.approvalDate : null);
    this.actionPlanForm?.setConclusionDate(typeof response !== 'number' ? response?.conclusionDate : null);
    this.modalConfig.mode = this.modalModes.view;
    // A draft plan is not yet a real action plan, so `GET /ActionPlan/{id}`
    // returns 404 for it — skip the reload and keep the created state.
    if (createdActionPlanUniqueId && !this.modalConfig?.isDraft) {
      this.modalConfig.id = createdActionPlanUniqueId;
      this.#loadActionPlanById();
    } else {
      this.#setMoreOptions();
      this.#trackRumSubmitLoaded();
    }
    this.#dispatchEventToLegacy('InsertedPlanCallback', createdActionPlanUniqueId, this.actionPlanForm?.selectedSystemUniqueId ?? this.loadedActionPlan?.systemUniqueId);
    // Refresh the history tab so users sitting on it see the "included"
    // entry without having to switch tabs (matches the behaviour of every
    // other state-changing action — escalate / reschedule / approve / etc).
    this.refreshHistory();
    this.#toastService.success(this.#translocoService.translate('Plano de ação incluído com sucesso'));
  }

  #onUpdateSuccess(actionPlan: SvcModalActionPlanByIdResponse): void {
    this.hasChanges = true;
    this.modalConfig.id = Number(actionPlan?.actionPlanUniqueId ?? this.modalConfig?.id ?? 0) || this.modalConfig?.id;
    this.modalConfig.mode = this.modalModes.view;
    this.#applyLoadedActionPlan(actionPlan);
    this.actionPlanForm?.patchFormActionPlan(actionPlan);
    this.#dispatchEventToLegacy('UpdatedPlanCallback', this.#toNullableNumber(actionPlan?.actionPlanUniqueId), actionPlan?.systemUniqueId);
    // Same reason as #onSaveSuccess: keep the history tab live without
    // requiring a tab toggle.
    this.refreshHistory();
    this.#toastService.success(this.#translocoService.translate('Alteração realizada com sucesso'));
  }

  public closeModal(): void {
    if (this.isLoading) return;
    if (this.checklistTab?.commitChecklistItem?.()) this.hasChanges = true;
    if (this.hasChanges && !this.#refreshSynced) this.#refresh.requestRefresh();
    this.dialogRef.close({ hadChanges: this.hasChanges, refreshed: this.#refreshSynced });
  }

  #broadcastRefresh(): void {
    if (!this.#refresh.isRealtimeConnected()) this.#refresh.requestRefresh();
    this.#refreshSynced = true;
  }

  #notifyRowTotalsUpdated(): void {
    const actionPlanUniqueId = this.#resolveCurrentActionPlanUniqueId();
    if (!Number.isFinite(actionPlanUniqueId) || actionPlanUniqueId <= 0) return;
    this.#refresh.notifyRowTotalsUpdated(actionPlanUniqueId);
  }

  public openEditMode(): void {
    if (!this.loadedActionPlan) return;
    if (!this.#permissions.canEdit(this.loadedActionPlan)) return;

    this.modalConfig.mode = this.modalModes.edit;
    this.#setMoreOptions();
  }

  #saveCustomFieldsByRecordId(recordId: number): Observable<boolean> {
    if (!recordId || !this.actionPlanForm?.hasCustomFields()) return of(true);
    return this.actionPlanForm.saveCustomFields(recordId);
  }

  #resolveCreatedActionPlanUniqueId(response: SvcModalActionPlanCreateResponse | number): Observable<number | null> {
    const directUniqueId = this.#resolvePlanReferenceId(response);
    if (directUniqueId) {
      return of(directUniqueId);
    }

    if (typeof response === 'number') {
      return of(null);
    }

    const actionPlanId = Number(response?.actionPlanId ?? 0);
    if (!Number.isFinite(actionPlanId) || actionPlanId <= 0) {
      return of(null);
    }

    return this.#modalActionPlanService.findActionPlanUniqueIdByActionPlanId(actionPlanId);
  }

  #resolveCurrentActionPlanUniqueId(): number {
    const rawActionPlanUniqueId = [this.modalConfig?.id, this.loadedActionPlan?.actionPlanUniqueId].find((value) => value !== null && value !== undefined);

    return Number(rawActionPlanUniqueId ?? 0);
  }

  #preserveApproverFromCurrentActionPlan(actionPlan: SvcModalActionPlanByIdResponse): SvcModalActionPlanByIdResponse {
    if (this.#permissions.hasAssignedApprover(actionPlan) || !this.#permissions.hasAssignedApprover(this.loadedActionPlan)) {
      return actionPlan;
    }
    return {
      ...actionPlan,
      approverUsers: this.#permissions.getApproverIds(this.loadedActionPlan),
    };
  }

  #resolvePlanReferenceId(response: SvcModalActionPlanCreateResponse | number): number | null {
    if (typeof response === 'number') return response;

    const rawReferenceId = [response?.actionPlanUniqueId, response?.planUniqueId, response?.planId, response?.id, response?.planUniqueReferenceId].find(
      (value) => value !== null && value !== undefined
    );
    const resolvedId = Number(rawReferenceId ?? null);
    return Number.isFinite(resolvedId) && resolvedId > 0 ? resolvedId : null;
  }

  #loadActionPlanById(): void {
    const actionPlanUniqueId = Number(this.modalConfig?.id ?? 0);
    if (!Number.isFinite(actionPlanUniqueId) || actionPlanUniqueId <= 0) return;

    if (this.modalConfig?.mode === this.modalModes.add) {
      this.modalConfig.mode = this.modalModes.edit;
    }

    this.isLoading = true;
    this.#modalActionPlanService
      .getActionPlanById(actionPlanUniqueId, this.modalConfig?.isDraft ?? false)
      .pipe(
        tap((actionPlan) => this.#onLoadActionPlanSuccess(actionPlan, actionPlanUniqueId)),
        catchError((err) => {
          this.#rumSubmitStartedAt = null;
          this.#errorService.showErrorInToast(err);
          return of(null);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #onLoadActionPlanSuccess(actionPlan: SvcModalActionPlanByIdResponse | null, fallbackId: number): void {
    if (!actionPlan) return;

    this.#applyLoadedActionPlan(actionPlan);
    this.modalConfig.id = Number(actionPlan?.actionPlanUniqueId ?? fallbackId) || fallbackId;

    const hasEditPermission = this.#permissions.canEdit(this.loadedActionPlan);
    if (this.modalConfig.mode === this.modalModes.view || !hasEditPermission) {
      this.modalConfig.mode = this.modalModes.view;
    } else {
      this.modalConfig.mode = this.modalModes.edit;
    }

    this.actionPlanForm?.patchFormActionPlan(actionPlan);
    this.#setMoreOptions();
    this.#trackRumRecordLoaded();
    this.#loadChecklistData(actionPlan.actionPlanUniqueId);
  }

  #loadChecklistData(actionPlanUniqueId: number | undefined): void {
    if (!actionPlanUniqueId) {
      this.loadedChecklist = null;
      return;
    }
    this.#checklistService
      .getChecklist(actionPlanUniqueId)
      .pipe(
        tap((response) => (this.loadedChecklist = response)),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          this.loadedChecklist = null;
          return of(null);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public confirmDeleteActionPlan(): void {
    if (!this.canDeleteActionPlan || this.isLoading) return;

    const text = this.#translocoService.translate('Tem certeza que deseja remover?');
    this.#dialogService
      .openConfirm(text)
      .pipe(take(1))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.#deleteActionPlan();
      });
  }

  #deleteActionPlan(): void {
    const actionPlanUniqueId = this.#resolveCurrentActionPlanUniqueId();
    if (!Number.isFinite(actionPlanUniqueId) || actionPlanUniqueId <= 0) return;

    this.isLoading = true;
    this.#modalActionPlanService
      .deleteActionPlan(actionPlanUniqueId)
      .pipe(
        tap(() => {
          this.hasChanges = true;
          this.#dispatchEventToLegacy('DeletedPlanCallback', actionPlanUniqueId, this.loadedActionPlan?.systemUniqueId);
          this.#toastService.success(this.#translocoService.translate('Registro removido com sucesso'));
          this.dialogRef.close({ hadChanges: true, deleted: true });
        }),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of(null);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public confirmCancelActionPlan(): void {
    if (!this.canCancelActionPlan || this.isLoading) return;

    const text = this.#translocoService.translate('Tem certeza que deseja cancelar?');
    this.#dialogService
      .openConfirm(text)
      .pipe(take(1))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.#cancelActionPlan();
      });
  }

  #cancelActionPlan(): void {
    const actionPlanUniqueId = this.#resolveCurrentActionPlanUniqueId();
    if (!Number.isFinite(actionPlanUniqueId) || actionPlanUniqueId <= 0 || this.isLoading) return;

    this.isLoading = true;
    this.#modalActionPlanService
      .cancelActionPlan(actionPlanUniqueId)
      .pipe(
        switchMap(() => this.#modalActionPlanService.getActionPlanById(actionPlanUniqueId)),
        tap((actionPlan) => {
          const resolvedActionPlan = this.#preserveApproverFromCurrentActionPlan(actionPlan);
          this.#applyLoadedActionPlan(resolvedActionPlan);
          this.actionPlanForm?.patchFormActionPlan(resolvedActionPlan);
          this.refreshHistory();
          // Deferred refresh: only set the change flag here. The outer list
          // reloads once on closeModal() instead of after every action.
          this.hasChanges = true;
          this.#setMoreOptions();
          this.#toastService.success(this.#translocoService.translate('Alteração realizada com sucesso'));
        }),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of(null);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #applyEscalateUpdate(result: SvcActionPlanEscalateModalResult): void {
    if (!result || this.isLoading) return;

    const actionPlanUniqueId = this.#resolveCurrentActionPlanUniqueId();
    const canUpdateByEscalate = Number.isFinite(actionPlanUniqueId) && actionPlanUniqueId > 0;

    const selectedResponsibleId = Array.isArray(result?.responsibleIds) ? String(result.responsibleIds[0] ?? '').trim() : '';
    if (!canUpdateByEscalate) return;

    const currentSystemId =
      this.actionPlanForm?.selectedSystemUniqueId ?? this.actionPlanForm?.formActionPlan?.getRawValue()?.systemId?.[0] ?? this.loadedActionPlan?.systemUniqueId ?? 0;
    const resolvedSystemId = Number(result?.obeya?.systemUniqueId ?? result?.systemId ?? currentSystemId);
    if (!selectedResponsibleId || !Number.isFinite(resolvedSystemId) || resolvedSystemId <= 0) return;

    const payload: SvcModalActionPlanEscalateRequest = {
      newResponsibleUserId: selectedResponsibleId,
      newSystemUniqueId: resolvedSystemId,
      comments: result?.comment ?? null,
      isObeya: !!result?.obeya,
      siteId: Number(result?.obeya?.siteId ?? this.loadedActionPlan?.siteId ?? this.#modalActionPlanService.getCurrentSiteId() ?? 0),
      ...(result?.obeya ? { systemReferences: buildObeyaSystemReferences(result.obeya.roomId, result.obeya.boardId) } : {}),
    };

    this.isLoading = true;
    this.#modalActionPlanService
      .escalateActionPlan(actionPlanUniqueId, payload)
      .pipe(
        switchMap(() => this.#modalActionPlanService.getActionPlanById(actionPlanUniqueId)),
        tap((actionPlan) => {
          if (!actionPlan) return;
          this.#applyLoadedActionPlan(actionPlan);
          this.actionPlanForm?.suppressNextCustomFieldsReload();
          this.actionPlanForm?.patchFormActionPlan(actionPlan);
          this.refreshHistory();
          this.hasChanges = true;
          this.#broadcastRefresh();
          this.#toastService.success(this.#translocoService.translate('Alteração realizada com sucesso'));
        }),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of(null);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #applyRescheduleUpdate(result: SvcActionPlanRescheduleModalResult): void {
    if (!result?.dueDate || this.isLoading) return;

    const actionPlanUniqueId = this.#resolveCurrentActionPlanUniqueId();
    const canReschedule = Number.isFinite(actionPlanUniqueId) && actionPlanUniqueId > 0;

    if (!canReschedule) {
      this.actionPlanForm?.formActionPlan?.controls?.dueDate?.setValue(result.dueDate);
      return;
    }

    this.isLoading = true;
    this.#modalActionPlanService
      .rescheduleActionPlan(actionPlanUniqueId, {
        previsionDate: result.dueDate,
        comments: result.comment ?? null,
      })
      .pipe(
        switchMap(() => this.#modalActionPlanService.getActionPlanById(actionPlanUniqueId)),
        tap((actionPlan) => {
          if (!actionPlan) return;
          this.#applyLoadedActionPlan(actionPlan);
          this.actionPlanForm?.patchFormActionPlan(actionPlan);
          this.refreshHistory();
          this.hasChanges = true;
          this.#broadcastRefresh();
          this.#toastService.success(this.#translocoService.translate('Alteração realizada com sucesso'));
        }),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of(null);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #toNullableNumber(value: string | number | null | undefined): number | null {
    const parsedValue = Number(value ?? null);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  #dispatchEventToLegacy(
    eventName:
      'InsertedPlanCallback' | 'UpdatedPlanCallback' | 'EndedPlanCallback' | 'ApprovedPlanCallback' | 'DisapprovedPlanCallback' | 'ReopenedPlanCallback' | 'DeletedPlanCallback',
    actionPlanUniqueId: number | null,
    systemUniqueId: number | null | undefined
  ): void {
    if (actionPlanUniqueId === null || !Number.isFinite(actionPlanUniqueId) || actionPlanUniqueId <= 0) return;
    const notificationParams = this.modalConfig?.notificationParams;
    globalThis.parent?.dispatchEvent(
      new CustomEvent(eventName, {
        detail: {
          actionPlanUniqueId,
          applicationId: notificationParams?.applicationId ?? null,
          referenceTypeId: notificationParams?.referenceTypeId ?? null,
          systemUniqueId: systemUniqueId ?? null,
        },
      })
    );
  }
}
